import {computed, ref, watch} from "vue";
import {months} from "@/models/Months";
import {useUserStore} from "@/stores/userStore";
import {useRoute} from "vue-router";

export const useCalendar = () => {

    const {formatDate} = useUserStore();

    const fromDate = ref<Date | null>(null);
    const toDate = ref<Date | null>(null);
    const hoverToDate = ref<Date | null>(null);
    const selectedOption = ref<'Dates' | 'Flexible'>('Dates');
    const howManyWeeks = ref<number>(null);
    const selectedFlexiblePeriod = ref<string>('');
    const selectedFlexibleMonth = ref<any>(null);
    const route = useRoute();
    const isYachtPage = computed(() => route.path.startsWith("/yachts"));

    const currentMonthIndex = new Date().getMonth();
    const sortedMonths = ref([]);
    sortedMonths.value = [
        ...months.slice(currentMonthIndex + 1),
        ...months.slice(0, currentMonthIndex + 1)
    ];

    const formatDateForYachtPage = (date) => {
        if (!date) return "";
        return new Intl.DateTimeFormat("en", {
            weekday: "short",
            month: "short",
            day: "2-digit",
            year: "numeric",
        }).format(new Date(date));
    };
    
    const dateRange = computed(() => {
        const dates = [];
        if (fromDate.value !== null && toDate.value === null) {
            if (hoverToDate.value !== null) {
                dates.push([fromDate.value, hoverToDate.value])
            } else {
                dates.push(fromDate.value)
            }
        } else if (fromDate.value === null && toDate.value !== null) {
            dates.push(toDate.value)
        } else if (fromDate.value !== null && toDate.value !== null) {
            if (fromDate.value > toDate.value) {
                dates.push([toDate.value, fromDate.value])
            } else if (fromDate.value < toDate.value) {
                dates.push([fromDate.value, toDate.value])
            } else {
                dates.push(fromDate.value)
            }
        }

        return [
            {
                highlight: '#1B84FF',
                dates: dates
            }
        ]
    });

    const startString = computed(() => {
        if (fromDate.value !== null) {
            return isYachtPage.value ? formatDateForYachtPage(fromDate.value) : formatDate(fromDate.value);
        }
        return "";
    });

    const endString = computed(() => {
        if (toDate.value !== null) {
            return isYachtPage.value ? formatDateForYachtPage(toDate.value) : formatDate(toDate.value);
        }
        return "";
    });

    const text = computed({
        get() {
            if (startString.value !== '' && endString.value !== '') {
                return startString.value + ' > ' + endString.value;
            } else if (startString.value !== '') {
                return startString.value;
            }
            return '';
        },
        set(value: string) {
            // Reset startString and endString if value is cleared
            if (value === '') {
                startString.value = '';
                endString.value = '';
            }
        },
    });

    const hasDates = computed(() => {
        return fromDate.value !== null && toDate.value !== null;
    })

    const clearDates = () => {
        fromDate.value = null;
        toDate.value = null;
        hoverToDate.value = null;
        
        // When we clear dates we reset it here to the first week of the month.
        // howManyWeeks.value=1;
        // selectedOption.value = 'Dates';
        selectedFlexibleMonth.value = null;
        selectedFlexiblePeriod.value='';
    }

    const getNextSaturdays = () => {
        if (howManyWeeks.value == null) return;

        if (selectedFlexibleMonth.value) {
            const selectedMonthObj = selectedFlexibleMonth.value.id;

            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth() + 1;

            // Check if the month we select is from the next year
            const targetYear = selectedMonthObj <= currentMonth ? currentYear + 1 : currentYear;

            const firstDayOfMonth = new Date(targetYear, selectedMonthObj - 1, 1);

            let thisSaturday = new Date(firstDayOfMonth);
            thisSaturday.setDate(thisSaturday.getDate() + (7 + (7 - thisSaturday.getDay())) % 7);
            thisSaturday.setUTCHours(0, 0, 0, 0);

            const nextSaturday = new Date(thisSaturday);
            nextSaturday.setDate(thisSaturday.getDate() + (7 * howManyWeeks.value));

            fromDate.value = thisSaturday;
            toDate.value = nextSaturday;

        } else {
            let thisSaturday = new Date();
            thisSaturday.setDate(thisSaturday.getDate() + (7 + (7 - thisSaturday.getDay())) % 7);
            thisSaturday.setUTCHours(0, 0, 0, 0)

            const nextSaturday = new Date(thisSaturday);
            nextSaturday.setDate(thisSaturday.getDate() + (7 * howManyWeeks.value));

            fromDate.value = thisSaturday;
            toDate.value = nextSaturday;
        }
    }

    const setDate = (date) => {

      if (fromDate.value === null) {
        fromDate.value = date;
      } else if (toDate.value === null) {
        toDate.value = date;
      } else {
        fromDate.value = date;
        toDate.value = null;
      }

      if (fromDate.value !== null && toDate.value !== null) {
        if (fromDate.value > toDate.value) {
          const tmp = fromDate.value;
          fromDate.value = toDate.value;
          toDate.value = tmp;
        }
      }


    }

    const setMonth = (month) => {
      if (selectedFlexibleMonth.value == month) {
        selectedFlexibleMonth.value = null;
      } else {
        selectedFlexibleMonth.value = month;
      }
    }


    watch([howManyWeeks, selectedFlexibleMonth], () => {
        getNextSaturdays()
    })


    return {
        text,
        hasDates,
        fromDate,
        toDate,
        howManyWeeks,
        startString,
        endString,
        hoverToDate,
        sortedMonths,
        dateRange,
        selectedOption,
        selectedFlexiblePeriod,
        selectedFlexibleMonth,
        getNextSaturdays,
        clearDates,
        setDate,
        setMonth
    }

}